import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Plus, Trash2, RefreshCw, Eye, Tag } from "lucide-react";
import { Button, Badge, FormField, Input } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { Modal } from "@/components/ui/Modal";
import { folderTemplatesApi } from "@/api/folders";
import { useDialogs } from "@/hooks/useDialogs";
import type { FolderTemplateDto, BackfillResultDto, FolderNameResyncResultDto } from "@/types/api";

// Bugfix Bundle B — surface every templated entity type in the rail so
// admins can edit Lead/Deal/Person/Entity/Business templates from the
// UI. Order matches the canonical ownership lifecycle: pre-deal (Lead),
// deal-stage (Deal), then the counterparty / financial-vehicle types
// alphabetically grouped behind them.
const ENTITY_TYPES = [
  "Lead",
  "Deal",
  "Loan",
  "Broker",
  "Borrower",
  "Person",
  "Entity",
  "Business",
  "Vendor",
  "Portfolio",
  "SPV",
  "Company",
];

/**
 * DMS v1.0 §5g — Admin-only folder template editor at
 * /admin/folder-templates.
 */
export function FolderTemplatesPage() {
  const qc = useQueryClient();
  const dialogs = useDialogs();
  const [selectedEntity, setSelectedEntity] = useState<string>(ENTITY_TYPES[0]);
  const [backfillResult, setBackfillResult] = useState<BackfillResultDto | null>(null);
  const [confirmBackfill, setConfirmBackfill] = useState(false);
  const [resyncResult, setResyncResult] = useState<FolderNameResyncResultDto | null>(null);
  const [confirmResync, setConfirmResync] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  const templates = useQuery({
    queryKey: ["dms", "templates", selectedEntity],
    queryFn: () => folderTemplatesApi.listByEntity(selectedEntity),
  });

  const counts = useQuery({
    queryKey: ["dms", "templateCounts"],
    queryFn: () => folderTemplatesApi.listAll(),
  });

  const create = useMutation({
    mutationFn: (name: string) =>
      folderTemplatesApi.create({
        entityType: selectedEntity,
        folderName: name,
        parentTemplateFolderName: null,
        sortOrder: (templates.data?.length ?? 0) + 1,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dms", "templates"] });
      qc.invalidateQueries({ queryKey: ["dms", "templateCounts"] });
      setNewFolderName("");
    },
  });

  const update = useMutation({
    mutationFn: (payload: { id: string; patch: Partial<FolderTemplateDto> & { folderName: string; sortOrder: number; isActive: boolean } }) =>
      folderTemplatesApi.update(payload.id, {
        folderName: payload.patch.folderName,
        parentTemplateFolderName: payload.patch.parentTemplateFolderName ?? null,
        sortOrder: payload.patch.sortOrder,
        isActive: payload.patch.isActive,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dms", "templates"] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => folderTemplatesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dms", "templates"] });
      qc.invalidateQueries({ queryKey: ["dms", "templateCounts"] });
    },
  });

  const backfill = useMutation({
    mutationFn: () => folderTemplatesApi.backfill(selectedEntity),
    onSuccess: (result) => {
      setBackfillResult(result);
      setConfirmBackfill(false);
    },
  });

  const resyncNames = useMutation({
    mutationFn: () => folderTemplatesApi.resyncNames(selectedEntity),
    // no-invalidation: renames existing entity-root DMS folders; this page
    // only reads folder-TEMPLATE rows (queried above), never actual folder
    // rows, so nothing rendered here goes stale.
    onSuccess: (result) => {
      setResyncResult(result);
      setConfirmResync(false);
    },
  });

  const countsByEntity = (counts.data ?? []).reduce<Record<string, number>>((acc, t) => {
    acc[t.entityType] = (acc[t.entityType] ?? 0) + (t.isActive ? 1 : 0);
    return acc;
  }, {});

  const active = (templates.data ?? []).filter((t) => t.parentTemplateFolderName == null);

  return (
    <>
      <PageHeader
        title="Folder templates"
        description="Default subfolders applied when new entities are created. Changes only affect new entities unless backfilled."
        actions={
          <div className="flex items-center gap-space-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setConfirmBackfill(true)}
              disabled={!templates.data || templates.data.length === 0}
            >
              <RefreshCw size={14} aria-hidden /> Apply to existing
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setConfirmResync(true)}
            >
              <Tag size={14} aria-hidden /> Re-sync folder names
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-12 gap-space-4">
        <aside className="col-span-12 md:col-span-3">
          <ul className="flex flex-col gap-space-1">
            {ENTITY_TYPES.map((et) => (
              <li key={et}>
                <button
                  type="button"
                  onClick={() => setSelectedEntity(et)}
                  className={`w-full px-space-3 py-space-2 rounded-md text-left text-small ${
                    et === selectedEntity
                      ? "bg-gold-light font-semibold text-onyx"
                      : "text-onyx hover:bg-warm-white"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{et}</span>
                    <Badge status="info">{countsByEntity[et] ?? 0}</Badge>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="col-span-12 md:col-span-9">
          <div className="flex items-center gap-space-2 mb-space-3">
            <h2 className="font-display font-bold text-h3 text-onyx m-0">{selectedEntity}</h2>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => setPreviewOpen(true)}
            >
              <Eye size={14} aria-hidden /> Preview tree
            </Button>
          </div>

          {templates.isLoading ? (
            <p className="text-caption text-gray-mid">Loading…</p>
          ) : (
            <ul className="flex flex-col gap-space-2">
              {active.map((t) => (
                <TemplateRow
                  key={t.id}
                  template={t}
                  onSave={(patch) =>
                    update.mutate({
                      id: t.id,
                      patch: {
                        ...patch,
                        folderName: patch.folderName,
                        sortOrder: patch.sortOrder,
                        isActive: patch.isActive,
                      },
                    })
                  }
                  onDelete={async () => {
                    const ok = await dialogs.confirm({
                      title: "Delete template?",
                      message: `Delete template "${t.folderName}" for new ${selectedEntity}s?`,
                      confirmLabel: "Delete",
                      destructive: true,
                    });
                    if (ok) remove.mutate(t.id);
                  }}
                />
              ))}
            </ul>
          )}

          <div className="mt-space-3 flex items-end gap-space-2">
            <FormField label="Add subfolder" htmlFor="new-tmpl-name" className="flex-1">
              <Input
                id="new-tmpl-name"
                placeholder="Folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newFolderName.trim()) {
                    create.mutate(newFolderName.trim());
                  }
                }}
              />
            </FormField>
            <Button
              disabled={!newFolderName.trim()}
              onClick={() => create.mutate(newFolderName.trim())}
            >
              <Plus size={14} aria-hidden /> Add folder
            </Button>
          </div>
        </section>
      </div>

      <Modal
        open={confirmBackfill}
        onClose={() => setConfirmBackfill(false)}
        title={`Apply all templates to existing ${selectedEntity}s`}
        description="This will create missing subfolders for every existing entity. Existing folders are never renamed or deleted."
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmBackfill(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => backfill.mutate()}
              disabled={backfill.isPending}
            >
              {backfill.isPending ? "Backfilling…" : "Proceed"}
            </Button>
          </>
        }
      >
        <p className="text-small text-gray-mid">
          Templates to apply: {active.length}. The operation is idempotent —
          entities that already have a subfolder of the same name are skipped.
        </p>
      </Modal>

      <Modal
        open={!!backfillResult}
        onClose={() => setBackfillResult(null)}
        title="Backfill complete"
        size="sm"
      >
        {backfillResult && (
          <ul className="text-small">
            <li>Entities processed: {backfillResult.entitiesProcessed}</li>
            <li>Folders created: {backfillResult.foldersCreated}</li>
            <li>Folders skipped: {backfillResult.foldersSkipped}</li>
          </ul>
        )}
      </Modal>

      <Modal
        open={confirmResync}
        onClose={() => setConfirmResync(false)}
        title={`Re-sync folder names for existing ${selectedEntity}s`}
        description="Renames every existing root folder to the entity's current display name. Subfolders and documents are not touched."
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmResync(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => resyncNames.mutate()}
              disabled={resyncNames.isPending}
            >
              {resyncNames.isPending ? "Re-syncing…" : "Proceed"}
            </Button>
          </>
        }
      >
        <p className="text-small text-gray-mid">
          The operation is idempotent — a root already named correctly is
          left alone, and a root whose owner can&apos;t be resolved (deleted
          or archived) is left untouched.
        </p>
      </Modal>

      <Modal
        open={!!resyncResult}
        onClose={() => setResyncResult(null)}
        title="Re-sync complete"
        size="sm"
      >
        {resyncResult && (
          <ul className="text-small">
            <li>Roots checked: {resyncResult.roots}</li>
            <li>Renamed: {resyncResult.renamed}</li>
            <li>Already current: {resyncResult.skipped}</li>
            <li>Unresolved: {resyncResult.unresolved}</li>
          </ul>
        )}
      </Modal>

      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={`Preview: new ${selectedEntity} folder tree`}
        size="md"
      >
        <ul className="text-small text-onyx">
          <li className="font-semibold">{selectedEntity} root</li>
          {active.map((t) => (
            <li key={t.id} className="ml-space-4">
              ├── {t.folderName}
            </li>
          ))}
        </ul>
      </Modal>
    </>
  );
}

interface TemplateRowProps {
  template: FolderTemplateDto;
  onSave: (patch: { folderName: string; sortOrder: number; isActive: boolean }) => void;
  onDelete: () => void;
}

function TemplateRow({ template, onSave, onDelete }: TemplateRowProps) {
  const [name, setName] = useState(template.folderName);
  const [isActive, setIsActive] = useState(template.isActive);
  const dirty = name !== template.folderName || isActive !== template.isActive;

  return (
    <li className="flex items-center gap-space-2 p-space-2 border border-border-light rounded-md bg-white-pure">
      <span className="text-caption text-gray-mid w-space-5 text-right">{template.sortOrder}</span>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        className={!isActive ? "line-through text-gray-mid" : undefined}
      />
      <label className="flex items-center gap-space-1 text-caption">
        <input
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
        />
        Active
      </label>
      <Button
        size="sm"
        variant="secondary"
        disabled={!dirty}
        onClick={() => onSave({ folderName: name, sortOrder: template.sortOrder, isActive })}
      >
        Save
      </Button>
      <Button size="sm" variant="icon" onClick={onDelete} aria-label="Delete">
        <Trash2 size={14} aria-hidden />
      </Button>
    </li>
  );
}
